// Packages
import React, { useState, useEffect, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

// MUI
import { useTheme } from "@mui/material/styles";
import {
  Divider,
  Grid,
  Box,
  Typography,
} from "@mui/material";

// Components
import EditModalAP from "../../Modal/EditModalAP";
import EditModalSwitch from "../../Modal/EditModalSwitch";
import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import ColumnVisibilityToggle from "components/Table/ColumnVisibilityToggle";
import { SolidRectangleLightButton } from "components/buttons/SolidRectangleLightButton";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import DrawerField from "components/Drawer";
import { Loading } from 'components/Loading';

// IMG
import { ReactComponent as EditIcon } from "assets/images/icons/icn_edit.svg";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useTableInstance from "hooks/useTableInstance";

// Const
import { DEVICE_ATTR } from "const";
import {
  getPortsTableColumns,
  defaultModels,
  columnVisibilityDefinition,
  defaultActions,
} from "../../const";

// Utils
import {
  getMainSendRequestParams,
  getInitCategory,
  getInitModels,
  getInitDefaultConfig,
  getInitSelectedConfig,
  getCleanPayload,
  getSendRequestParams
} from "../../helper";

const CreateTemplate = ({
  dataMap,
  getData,
  sendRequest,
  alertInfo,
  setAlertInfo,
  actions,
  setActions,
  selectedConfig,
  setSelectedConfig,
  showEditGeneral,
  onGoToPage
}) => {
  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { auth, isAuthReady } = useAuthScope();

  const [portsUuids] = useState(() => ({
    create: uuidv4(),
    update: uuidv4(),
    readModels: uuidv4(),
    readDefaultConfig: uuidv4(),
    readMacProfile: uuidv4(),
    readRadiusProfile: uuidv4(),
    readScheduleProfile: uuidv4(),
  }));

  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [macProfiles, setMacProfiles] = useState([]);
  const [radiusProfiles, setRadiusProfiles] = useState([]);
  const [scheduleProfiles, setScheduleProfiles] = useState([]);

  // Category
  const initCategories = useMemo(() => getInitCategory({ config: selectedConfig }), [selectedConfig]);
  const [categories, setCategories] = useState(initCategories);
  const selectedCategory = useMemo(() => categories.find(category => category.isSelected), [categories]);

  // Models
  const [originalModels, setOriginalModels] = useState([]);
  const [models, setModels] = useState(defaultModels);
  const selectedModel = useMemo(() => models.find(model => model.isSelected), [models]);

  // Port Configs
  const [defaultConfig, setDefaultConfig] = useState({});
  const [portConfigs, setPortConfigs] = useState([]);

  const isNeedCombineModel = useMemo(() => portConfigs[0]?.isNeedCombineModel, [portConfigs]);
  const displayPortConfigs = useMemo(() => portConfigs ? portConfigs.filter(port => port.isShow) : portConfigs, [portConfigs, isNeedCombineModel]);

  const isEditMode = useMemo(() => !!selectedConfig, [selectedConfig]);

  const portsTableInstance = useTableInstance({
    tableName: "portsTable",
    data: displayPortConfigs,
    columns: getPortsTableColumns({
      auth,
      columnHelper,
      permissions: auth.permissions,
      theme,
      scheduleProfiles
    }),
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: [
      "modelName",
      "portIndex",
      "formattedFinalSpeedLabel",
      "formattedPortTypeLabel",
      "formattedAggregateLabel",
      "PVID",
      "allowVlan",
      "formattedPOEStateLabel",
      "formattedPOEUsageLabel",
      "formattedRSTPLabel",
      "formattedLBDStateLabel",
      "formattedDDPStateLabel",
      "lldp",
      "stpGuard",
      "portSchedule",
      "poeSchedule",
      "accessPolicies",
      "mirror",
      "portIsolate",
      "formattedPortTagsLabel",
      "flowControl",
    ],
    hiddenColumns: [
      "portSchedule",
      "poeSchedule",
      "accessPolicies",
      "mirror",
      "portIsolate",
      "portTags",
      "flowControl",
    ],
    enableSorting: true,
    enableColumnOrder: false,
    enableRowSelection: true,
    enablePagination: true,
  });

  const selectedRows = useMemo(() => portsTableInstance?.table?.getSelectedRowModel()?.rows, [portsTableInstance?.table?.getSelectedRowModel()?.rows]);
  const [selectedDrawerTarget, setSelectedDrawerTarget] = useState(null);

  const isFormInvalid = () => {
    let isInvalid = false;

    if (!selectedCategory?.value) isInvalid = true;
    if (!selectedModel?.value) isInvalid = true;

    return isInvalid;
  }

  const fetchDefaultConfig = () => {
    setIsFetching(true);

    const params = {
      data: {
        seriesName: selectedModel.seriesName
      }
    }

    sendRequest(
      "/system/wiredConfigProvisioning/defaultValues.read",
      portsUuids.readDefaultConfig,
      null,
      params
    );
  }

  const fetchModels = () => {
    setIsFetching(true);

    sendRequest(
      "/system/wiredConfigProvisioning/modelList.read",
      portsUuids.readModels
    );
  }

  const fetchProfiles = () => {
    setIsFetching(true);

    const macParams = getSendRequestParams({ auth, inFix: "profileMacAddrs" });
    sendRequest(macParams.apiUrl, portsUuids.readMacProfile, macParams.requestParams);

    const radParams = getSendRequestParams({ auth, inFix: "radiusProfile" });
    sendRequest(radParams.apiUrl, portsUuids.readRadiusProfile, radParams.requestParams);

    const schedParams = getSendRequestParams({ auth, inFix: "profileSchedule" });
    sendRequest(schedParams.apiUrl, portsUuids.readScheduleProfile, schedParams.requestParams);
  };

  const onCreateAndSave = () => {
    setIsFetching(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "create" });
    const payload = getCleanPayload({ defaultConfig, selectedConfig: null });

    sendRequest(
      apiUrl,
      portsUuids.create,
      requestParams,
      payload
    );
  }

  const onEditAndSave = () => {
    setIsFetching(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "update" });
    const payload = getCleanPayload({ defaultConfig, selectedConfig });

    sendRequest(
      apiUrl,
      portsUuids.update,
      requestParams,
      payload
    );
  }

  const onCloseDrawer = () => {
    if (actions.isOpenEditDrawer) setActions({ ...defaultActions, isOpenCreateTemplate: true, isOpenEditDrawer: false });
  }

  useEffect(() => {
    if (isAuthReady) {
      fetchModels();
      fetchProfiles();
    }
  }, [isAuthReady]);

  useEffect(() => {
    if (!dataMap?.[portsUuids.readMacProfile]) return;
    const response = getData(portsUuids.readMacProfile);
    if (response?.data) {
      setMacProfiles(response.data);
    }
  }, [dataMap?.[portsUuids.readMacProfile]]);

  useEffect(() => {
    if (!dataMap?.[portsUuids.readRadiusProfile]) return;
    const response = getData(portsUuids.readRadiusProfile);
    if (response?.data) {
      setRadiusProfiles(response.data);
    }
  }, [dataMap?.[portsUuids.readRadiusProfile]]);

  useEffect(() => {
    if (!dataMap?.[portsUuids.readScheduleProfile]) return;
    const response = getData(portsUuids.readScheduleProfile);
    if (response?.data) {
      setScheduleProfiles((response.data || []).filter((item) => item.profileType === 'wired').map(p => ({ label: p.name, value: p.id })));
    }
  }, [dataMap?.[portsUuids.readScheduleProfile]]);

  useEffect(() => {
    setModels(defaultModels);
    setDefaultConfig({});
    setPortConfigs([]);

    if (selectedCategory?.value) {
      setModels(getInitModels({
        models: originalModels,
        selectedCategory,
        selectedConfig
      }));
    }

    onCloseDrawer();
  }, [selectedConfig, selectedCategory?.value, originalModels]);

  useEffect(() => {
    if (defaultConfig?.portConfigs) setPortConfigs(defaultConfig.portConfigs);
  }, [defaultConfig?.portConfigs]);

  useEffect(() => {
    setDefaultConfig({});
    setPortConfigs([]);
    onCloseDrawer();

    if (selectedModel?.value) {
      fetchDefaultConfig();
    }
  }, [selectedModel?.value]);

  useEffect(() => {
    if (!dataMap?.[portsUuids.readModels]) return;

    const response = getData(portsUuids.readModels);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the device model list.",
        });
      } else {
        const sortedModels = [...responseData].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        setOriginalModels(sortedModels);
      }

      setIsFetching(false);
    }
  }, [dataMap?.[portsUuids.readModels]]);

  useEffect(() => {
    if (!dataMap?.[portsUuids.readDefaultConfig]) return;

    const response = getData(portsUuids.readDefaultConfig);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message ?? responseData?.message ?? "Failed to get the default config.",
        });
      } else {

        if (!selectedConfig) {
          // Create
          const rawConfig = getInitDefaultConfig({ responseConfig: responseData });
          if (rawConfig) {
            setDefaultConfig(rawConfig);
            setPortConfigs(rawConfig.portConfigs);
          }
        } else {
          // Edit
          const rawConfig = getInitSelectedConfig({ responseConfig: responseData, selectedConfig });
          if (rawConfig) {
            setDefaultConfig(rawConfig);
            setPortConfigs(rawConfig.portConfigs);
          }
        }
      }

      setIsFetching(false);
    }
  }, [dataMap?.[portsUuids.readDefaultConfig]]);

  useEffect(() => {
    if (!dataMap?.[portsUuids.update]) return;

    const response = getData(portsUuids.update);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message ?? responseData?.message ?? "Failed to update the default config.",
        });
      } else {
        setActions({ ...defaultActions, isSave: true });
      }

      setIsFetching(false);
    }
  }, [dataMap?.[portsUuids.update]]);

  useEffect(() => {
    if (!dataMap?.[portsUuids.create]) return;

    const response = getData(portsUuids.create);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData?.error?.message ?? responseData?.message ?? "Failed to create the default config.",
        });
      } else {
        setActions({ ...defaultActions, isSave: true });
      }

      setIsFetching(false);
    }
  }, [dataMap?.[portsUuids.create]]);

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  return (
    <Box>
      {isLoading && <Loading />}

      <Typography
        sx={{
          color: theme.palette.primary.main,
          fontSize: theme.typography.customFontSize[5],
          fontWeight: theme.typography.customFontWeight[1],
        }}
      >
        {`${isEditMode ? "Edit" : "Create"} Template`}
      </Typography>

      <Divider sx={{ margin: "20px 0" }} />

      <Box>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "16px",
              }}
            >
              {/* Edit button */}
              {
                selectedRows?.length > 0 &&
                showEditGeneral &&
                (
                  <>
                    <SolidRectangleLightButton
                      width="100px"
                      height="32px"
                      iconStart={<EditIcon />}
                      onClick={() => {
                        setActions({ ...defaultActions, isOpenCreateTemplate: true, isOpenEditModal: true });
                      }}
                    >
                      Edit
                    </SolidRectangleLightButton>
                    <Divider
                      sx={{
                        borderColor: theme.palette.primary.greyThread,
                        width: "1px",
                        margin: "0px 4px",
                        minHeight: "50px",
                      }}
                      orientation="vertical"
                      flexItem
                    />
                  </>
                )
              }

              {/* Category and model selector */}
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[2],
                  fontWeight: theme.typography.customFontWeight[1],
                }}
              >
                Select a model
              </Typography>

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <MenuSelect
                  placeholder="Select Category"
                  width="160px"
                  value={selectedCategory?.value}
                  options={categories}
                  disabled={isEditMode}
                  onChange={category => {
                    setCategories(prevCategories => (
                      prevCategories.map(loopCategory => ({
                        ...loopCategory,
                        isSelected: loopCategory.value === category.value
                      }))
                    ))
                  }}
                />

                <MenuSelect
                  placeholder="Selected Model"
                  width="230px"
                  value={selectedModel?.value}
                  options={models}
                  disabled={isEditMode}
                  onChange={model => {
                    setModels(prevModel => (
                      prevModel.map(loopModel => ({
                        ...loopModel,
                        isSelected: loopModel.value === model.value
                      }))
                    ))
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ height: "80px" }}>
            <Grid container sx={{ minHeight: "100%" }} alignItems="center" gap={1.5}>

              {/* Search */}
              <SearchInput
                value={portsTableInstance?.globalFilter}
                onChange={portsTableInstance?.setGlobalFilter}
                placeholder="Search by Name"
              />

              {/* Column visibility */}
              <ColumnVisibilityToggle
                maxWidth={600}
                {...portsTableInstance}
                groupedData={columnVisibilityDefinition}
                hiddenCheck={["modelName", "portIndex"]}
              />

            </Grid>
          </Box>

        </Grid>

        <TableField
          {...portsTableInstance}
          drawerOpen={actions.isOpenEditDrawer}
          handleRowOnClick={row => {
            setSelectedDrawerTarget(structuredClone(row));
            setActions({ ...defaultActions, isOpenCreateTemplate: true, isOpenEditDrawer: true });
          }}
        />


        <Box
          sx={{
            margin: "50px auto 30px",
            display: "flex",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          <OutlineButton
            onClick={() => {
              setActions(defaultActions);
              setSelectedConfig(null);
              setCategories(initCategories);
              setModels(structuredClone(originalModels));
            }}
            width="100px"
            height="32px"
          >
            Cancel
          </OutlineButton>
          <SolidButton
            disabled={isFormInvalid()}
            onClick={() => {
              if (!isEditMode) onCreateAndSave();
              else onEditAndSave();
            }}
            width="100px"
            height="32px"
          >
            Save
          </SolidButton>
        </Box>

        {
          actions.isOpenEditModal &&
          <>
            {
              selectedCategory?.value === DEVICE_ATTR.types.AP &&
              <EditModalAP
                {...{
                  auth,
                  alertInfo,
                  setAlertInfo,
                  defaultConfig,
                  setDefaultConfig,
                  actions,
                  setActions,
                  selectedRows,
                  showEditGeneral,
                }}
              />
            }

            {
              selectedCategory?.value === DEVICE_ATTR.types.SW &&
              <EditModalSwitch
                {...{
                  auth,
                  alertInfo,
                  setAlertInfo,
                  defaultConfig,
                  setDefaultConfig,
                  actions,
                  setActions,
                  selectedRows,
                  showEditGeneral,
                  macProfiles,
                  radiusProfiles,
                  scheduleProfiles,
                  onGoToPage
                }}
              />
            }
          </>
        }

        {
          actions.isOpenEditDrawer &&
          selectedDrawerTarget &&
          <DrawerField
            width={620}
            contentKey={`Settings_Config_Provisioning_Drawer_${selectedDrawerTarget?.id}`}
            open={actions.isOpenEditDrawer}
            handleClose={() => onCloseDrawer()}
            commonContent="settingsWiredConfigProvisioning"
            contentProps={{
              auth,
              alertInfo,
              setAlertInfo,
              defaultConfig,
              setDefaultConfig,
              selectedDrawerTarget,
              isOpenEditDrawer: actions.isOpenEditDrawer,
              showEditGeneral,
              selectedCategory,
              macProfiles,
              radiusProfiles,
              scheduleProfiles,
              onGoToPage,
              closeCallback: () => onCloseDrawer()
            }}
          />
        }
      </Box>

    </Box>
  )
};

export default CreateTemplate;
